import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faReceipt,
  faFileCsv,
  faChevronLeft,
  faChevronRight,
  faEye,
  faRefresh,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { streetlearnApi } from '../api';
import { showToast } from '../../components/toast';
import { API_URL } from '../../config';
import '../../Subscriber/subscribe.css';

// Helper function to parse single range string into min and max values (from Subscriber module)
const parseNumericRange = (value) => {
  if (!value || typeof value !== 'string') return { min: undefined, max: undefined };
  const trimmed = value.trim();
  if (!trimmed) return { min: undefined, max: undefined };

  // Match range like "100-200", "100 - 200", "100 to 200"
  const rangeMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*(?:-|to)\s*(\d+(?:\.\d+)?)$/i);
  if (rangeMatch) {
    return { min: rangeMatch[1], max: rangeMatch[2] };
  }

  // Match ">=100", ">100", "100+"
  const gteMatch = trimmed.match(/^(?:>=?|>)\s*(\d+(?:\.\d+)?)$/) || trimmed.match(/^(\d+(?:\.\d+)?)\+$/);
  if (gteMatch) {
    return { min: gteMatch[1], max: undefined };
  }

  // Match "<=200", "<200"
  const lteMatch = trimmed.match(/^(?:<=?|<)\s*(\d+(?:\.\d+)?)$/);
  if (lteMatch) {
    return { min: undefined, max: lteMatch[1] };
  }

  // Single number "100": set both min and max for exact match
  const singleMatch = trimmed.match(/^(\d+(?:\.\d+)?)$/);
  if (singleMatch) {
    return { min: singleMatch[1], max: singleMatch[1] };
  }

  return { min: undefined, max: undefined };
};

const formatDateForAPI = (date) => {
  if (!date) return undefined;
  return moment(date).format('YYYY-MM-DD');
};

const OrdersList = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);

  // Filters (Subscriber module style)
  const [search, setSearch] = useState('');
  const [phone, setPhone] = useState('');
  const [amountRangeFilter, setAmountRangeFilter] = useState('');
  const [status, setStatus] = useState('');
  const [paymentDateFilter, setPaymentDateFilter] = useState([null, null]);
  const [activationDateFilter, setActivationDateFilter] = useState([null, null]);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const parsedAmount = parseNumericRange(amountRangeFilter);
      const data = await streetlearnApi.getOrders({
        page,
        limit,
        q: search.trim() || undefined,
        phone: phone.trim() || undefined,
        amount_min: parsedAmount.min !== undefined ? Number(parsedAmount.min) : undefined,
        amount_max: parsedAmount.max !== undefined ? Number(parsedAmount.max) : undefined,
        status: status || undefined,
        pay_from: formatDateForAPI(paymentDateFilter[0]),
        pay_to: formatDateForAPI(paymentDateFilter[1] || paymentDateFilter[0]),
        act_from: formatDateForAPI(activationDateFilter[0]),
        act_to: formatDateForAPI(activationDateFilter[1] || activationDateFilter[0]),
      });
      setOrders(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      showToast.error(err.response?.data?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, phone, amountRangeFilter, status, paymentDateFilter, activationDateFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleExportCsv = () => {
    const token = localStorage.getItem('authToken');
    const parsedAmount = parseNumericRange(amountRangeFilter);
    const params = new URLSearchParams();
    if (search.trim()) params.append('q', search.trim());
    if (phone.trim()) params.append('phone', phone.trim());
    if (parsedAmount.min !== undefined) params.append('amount_min', parsedAmount.min);
    if (parsedAmount.max !== undefined) params.append('amount_max', parsedAmount.max);
    if (status) params.append('status', status);
    if (paymentDateFilter[0]) {
      params.append('pay_from', formatDateForAPI(paymentDateFilter[0]));
      params.append('pay_to', formatDateForAPI(paymentDateFilter[1] || paymentDateFilter[0]));
    }
    if (activationDateFilter[0]) {
      params.append('act_from', formatDateForAPI(activationDateFilter[0]));
      params.append('act_to', formatDateForAPI(activationDateFilter[1] || activationDateFilter[0]));
    }
    if (token) params.append('token', token);

    window.open(`${API_URL}streetlearn/orders/export?${params.toString()}`, '_blank');
  };

  const clearAllFilters = () => {
    setSearch('');
    setPhone('');
    setAmountRangeFilter('');
    setStatus('');
    setPaymentDateFilter([null, null]);
    setActivationDateFilter([null, null]);
    setPage(1);
  };

  const totalPages = Math.ceil(total / limit) || 1;
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  return (
    <div className="mt-5">
      <style>{`
        #datepicker-portal .react-datepicker-popper,
        .react-datepicker-popper {
          z-index: 99999 !important;
        }
        #datepicker-portal .react-datepicker {
          box-shadow: 0 4px 16px rgba(0,0,0,0.2) !important;
        }
      `}</style>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <div>
          <h4 className="panel-title m-0">
            <FontAwesomeIcon icon={faReceipt} className="me-2 text-primary" />
            StreetLearn Orders & Payments
          </h4>
        </div>
        <div className="d-flex align-items-center flex-wrap">
          <button className="btn btn-default btn-sm mx-1" onClick={fetchOrders} title="Refresh">
            <FontAwesomeIcon icon={faRefresh} spin={loading} /> Refresh
          </button>
          <button className="btn btn-default btn-sm mx-1" onClick={handleExportCsv}>
            <FontAwesomeIcon icon={faFileCsv} /> Export Orders CSV
          </button>

          {total > 0 && (
            <div className="d-inline-flex align-items-center ms-2">
              <span style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }} className="me-2">
                {start} to {end} of {total}
              </span>
              <button
                className="btn btn-default btn-sm"
                title="Previous"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              &nbsp;
              <button
                className="btn btn-default btn-sm"
                title="Next"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Table Panel (Exact Subscriber Module UI) */}
      <div className="panel panel-default with-rounded-corner with-shadow mb-3">
        <div className="panel-body p-0">
          <div className="table-responsive">
            <table className="table table-condensed table-hover bg-white sticky-table m-0">
              <thead>
                <tr>
                  <th style={{ minWidth: '150px' }}>Name</th>
                  <th style={{ minWidth: '120px' }}>Phone Number</th>
                  <th style={{ minWidth: '100px' }}>Amount</th>
                  <th style={{ minWidth: '90px' }}>Status</th>
                  <th style={{ minWidth: '120px' }}>Payment Date</th>
                  <th style={{ minWidth: '180px' }}>Activation / Expiry Date</th>
                  <th style={{ width: '80px', textAlign: 'center' }}>Action</th>
                </tr>
                {/* In-Table Filter Row */}
                <tr>
                  <th>
                    <input
                      type="text"
                      placeholder="Search name..."
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                      style={{ width: '100%', padding: '4px', fontSize: '11px' }}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      placeholder="Phone..."
                      value={phone}
                      onChange={(e) => {
                        setPhone(e.target.value);
                        setPage(1);
                      }}
                      style={{ width: '100%', padding: '4px', fontSize: '11px' }}
                    />
                  </th>
                  <th>
                    <input
                      type="text"
                      placeholder="e.g. 1000-50000"
                      title="Enter single value or range (e.g. 1000-50000, >=1000, <=50000)"
                      value={amountRangeFilter}
                      onChange={(e) => {
                        setAmountRangeFilter(e.target.value);
                        setPage(1);
                      }}
                      style={{
                        width: '100%',
                        padding: '5px',
                        fontSize: '12px',
                      }}
                    />
                  </th>
                  <th>
                    <select
                      value={status}
                      onChange={(e) => {
                        setStatus(e.target.value);
                        setPage(1);
                      }}
                      style={{ width: '100%', padding: '5px', fontSize: '12px' }}
                    >
                      <option value="">--select--</option>
                      <option value="Active">Active</option>
                      <option value="paid">Paid</option>
                      <option value="Pending">Pending</option>
                      <option value="created">Created</option>
                      <option value="failed">Failed</option>
                      <option value="refunded">Refunded</option>
                    </select>
                  </th>
                  <th>
                    <div className="d-flex align-items-center gap-1">
                      <div className="custom-datepicker-wrapper" style={{ width: '100%' }}>
                        <DatePicker
                          portalId="datepicker-portal"
                          selected={paymentDateFilter[0]}
                          onChange={(dates) => {
                            setPaymentDateFilter(dates);
                            setPage(1);
                          }}
                          startDate={paymentDateFilter[0]}
                          endDate={paymentDateFilter[1]}
                          selectsRange
                          isClearable
                          toggleCalendarOnIconClick
                          placeholderText="Select date range"
                          monthsShown={2}
                          openToDate={paymentDateFilter[0] || new Date()}
                          showYearDropdown
                          showMonthDropdown
                          dropdownMode="select"
                          scrollableYearDropdown
                          yearDropdownItemNumber={15}
                          dateFormat="dd-MM-yyyy"
                        />
                      </div>
                    </div>
                  </th>
                  <th>
                    <div className="d-flex align-items-center gap-1">
                      <div className="custom-datepicker-wrapper" style={{ width: '100%' }}>
                        <DatePicker
                          portalId="datepicker-portal"
                          selected={activationDateFilter[0]}
                          onChange={(dates) => {
                            setActivationDateFilter(dates);
                            setPage(1);
                          }}
                          startDate={activationDateFilter[0]}
                          endDate={activationDateFilter[1]}
                          selectsRange
                          isClearable
                          toggleCalendarOnIconClick
                          placeholderText="Select date range"
                          monthsShown={2}
                          openToDate={activationDateFilter[0] || new Date()}
                          showYearDropdown
                          showMonthDropdown
                          dropdownMode="select"
                          scrollableYearDropdown
                          yearDropdownItemNumber={15}
                          dateFormat="dd-MM-yyyy"
                        />
                      </div>
                    </div>
                  </th>
                  <th style={{ textAlign: 'center' }}>
                    <button
                      type="button"
                      className="btn btn-default btn-xs"
                      title="Clear all filters"
                      onClick={clearAllFilters}
                    >
                      Clear
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4">
                      <FontAwesomeIcon icon={faRefresh} spin className="me-2 text-primary" />
                      Loading orders...
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4 text-muted">
                      No orders found matching the filter criteria.
                    </td>
                  </tr>
                ) : (
                  orders.map((o) => (
                    <tr key={o.id}>
                      <td>
                        <span className="font-bold text-dark">
                          {o.learner_name || o.subscriber_name || '-'}
                        </span>
                      </td>
                      <td>
                        <span className="font-bold text-dark">
                          {o.learner_phone || o.subscriber_phone || '-'}
                        </span>
                      </td>
                      <td>
                        <span className="font-bold text-primary" style={{ fontSize: '13px' }}>
                          ₹{Number(o.amount_inr || 0).toFixed(2)}
                        </span>
                      </td>
                      <td>
                        {o.status === 'Active' || o.status === 'paid' ? (
                          <span className="text-success font-weight-bold">
                            {o.status === 'Active' ? 'Active' : 'Paid'}
                          </span>
                        ) : o.status === 'created' || o.status === 'Pending' ? (
                          <span className="text-warning font-weight-bold">Pending</span>
                        ) : o.status === 'refunded' ? (
                          <span className="text-info font-weight-bold">Refunded</span>
                        ) : (
                          <span className="text-danger font-weight-bold">{o.status}</span>
                        )}
                      </td>
                      <td className="text-muted small">
                        {o.paid_at
                          ? new Date(o.paid_at).toLocaleDateString()
                          : o.created_at
                          ? new Date(o.created_at).toLocaleDateString()
                          : '-'}
                      </td>
                      <td className="small">
                        <div>
                          <span className="text-muted">Start: </span>
                          <span className="text-dark font-weight-bold">
                            {o.activation_date
                              ? new Date(o.activation_date).toLocaleDateString()
                              : '-'}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted">End: </span>
                          <span className="text-dark font-weight-bold">
                            {o.expiry_date
                              ? new Date(o.expiry_date).toLocaleDateString()
                              : '-'}
                          </span>
                        </div>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {o.learner_id ? (
                          <button
                            className="btn btn-default btn-xs"
                            title="View Learner Profile"
                            onClick={() => navigate(`/streetlearn/learners/${o.learner_id}`)}
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </button>
                        ) : (
                          <span className="text-muted small">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrdersList;
